import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { stickerTextStyle } from './StickerText';
import { SparklesIcon, SearchIcon, CloseIcon } from './Icons';
import api from '../api';

export interface GifItem {
    id: string;
    url: string;
    previewUrl?: string;
    mp4Url?: string;
    name: string;
    source: 'tenor' | 'giphy';
}

export interface GifPickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectGif: (url: string, item?: GifItem) => void;
    title?: string;
    zIndexClass?: string;
}

export const GifPickerModal: React.FC<GifPickerModalProps> = ({
    isOpen,
    onClose,
    onSelectGif,
    title = 'Search GIFs',
    zIndexClass = 'z-[150]',
}) => {
    const { currentUser, usersData } = useAuth();
    const currentUserData = usersData ? usersData[currentUser] : undefined;
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const [searchQuery, setSearchQuery] = useState('');
    const [tenorResults, setTenorResults] = useState<GifItem[]>([]);
    const [giphyResults, setGiphyResults] = useState<GifItem[]>([]);
    const [loading, setLoading] = useState(false);

    // Keep track of latest active request ID to ignore stale out-of-order responses
    const latestRequestIdRef = React.useRef(0);
    // In-memory search cache for instant repeat results
    const searchCacheRef = React.useRef<Map<string, { tenor: GifItem[]; giphy: GifItem[] }>>(new Map());

    const searchTenorDirect = async (query: string, signal?: AbortSignal): Promise<GifItem[]> => {
        const cleanQuery = query.trim();
        
        // 1. Try public Tenor v1 endpoint (widely supported without API key restrictions)
        try {
            const v1Endpoint = cleanQuery
                ? `https://g.tenor.com/v1/search?q=${encodeURIComponent(cleanQuery)}&key=LIVDSRZULE6M&limit=20`
                : `https://g.tenor.com/v1/trending?key=LIVDSRZULE6M&limit=20`;
            const res = await fetch(v1Endpoint, { signal });
            if (res.ok) {
                const data = await res.json();
                if (data?.results && data.results.length > 0) {
                    return data.results.map((r: any) => ({
                        id: `tenor-${r.id}`,
                        url: r.media?.[0]?.gif?.url || r.media?.[0]?.mediumgif?.url || r.url || '',
                        previewUrl: r.media?.[0]?.nanogif?.url || r.media?.[0]?.tinygif?.url || r.media?.[0]?.gif?.url || '',
                        name: r.title || r.content_description || 'Tenor GIF',
                        source: 'tenor' as const,
                    })).filter((item: GifItem) => Boolean(item.url));
                }
            }
        } catch (e: any) {
            if (e?.name === 'AbortError') return [];
        }

        // 2. Fallback to backend /api/tenor/search
        try {
            const response = await api.get(`tenor/search?q=${encodeURIComponent(cleanQuery)}`, { signal });
            const data = response.data;
            if (data?.results && data.results.length > 0) {
                return data.results.map((r: any) => ({
                    id: `tenor-${r.id}`,
                    url: r.media?.[0]?.gif?.url || r.url || '',
                    previewUrl: r.media?.[0]?.tinygif?.url || r.media?.[0]?.gif?.url || r.url || '',
                    name: r.title || 'Tenor GIF',
                    source: 'tenor' as const,
                })).filter((item: GifItem) => Boolean(item.url));
            }
        } catch (e: any) {
            if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') {
                console.error('Tenor proxy fallback error:', e);
            }
        }

        return [];
    };

    const searchGiphyDirect = async (query: string, signal?: AbortSignal): Promise<GifItem[]> => {
        try {
            const GIPHY_KEY = 'Gc7131jiJuvI7IdN0HZ1D7nh0ow5BU6g';
            const cleanQuery = query.trim();
            const endpoint = cleanQuery
                ? `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_KEY}&q=${encodeURIComponent(cleanQuery)}&limit=20&rating=g`
                : `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_KEY}&limit=20&rating=g`;
            
            const response = await fetch(endpoint, { signal });
            const data = await response.json();
            if (!data?.data) return [];

            return data.data.map((r: any) => {
                const webpPreview = r.images?.fixed_width_small?.webp || r.images?.fixed_height_small?.webp || r.images?.fixed_height_small?.url || r.images?.fixed_width?.url;
                return {
                    id: `giphy-${r.id}`,
                    url: r.images?.original?.url || r.images?.fixed_height?.url || `https://i.giphy.com/${r.id}.gif`,
                    previewUrl: webpPreview || `https://i.giphy.com/${r.id}.gif`,
                    name: r.title || 'Giphy GIF',
                    source: 'giphy' as const,
                };
            }).filter((item: GifItem) => Boolean(item.url));
        } catch (e: any) {
            if (e?.name !== 'AbortError') {
                console.error('Giphy search error:', e);
            }
            return [];
        }
    };

    const executeSearch = useCallback(async (query: string, requestId: number, controller: AbortController) => {
        const cacheKey = query.trim().toLowerCase();
        if (searchCacheRef.current.has(cacheKey)) {
            const cached = searchCacheRef.current.get(cacheKey)!;
            setTenorResults(cached.tenor);
            setGiphyResults(cached.giphy);
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const [tenorRes, giphyRes] = await Promise.allSettled([
                searchTenorDirect(query, controller.signal),
                searchGiphyDirect(query, controller.signal),
            ]);

            if (requestId !== latestRequestIdRef.current) return;

            const tenorList = tenorRes.status === 'fulfilled' ? tenorRes.value : [];
            const giphyList = giphyRes.status === 'fulfilled' ? giphyRes.value : [];

            searchCacheRef.current.set(cacheKey, { tenor: tenorList, giphy: giphyList });
            setTenorResults(tenorList);
            setGiphyResults(giphyList);
        } catch (err) {
            if (requestId === latestRequestIdRef.current) {
                console.error('GIF search error:', err);
                setTenorResults([]);
                setGiphyResults([]);
            }
        } finally {
            if (requestId === latestRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        
        const controller = new AbortController();
        const requestId = ++latestRequestIdRef.current;

        const timer = setTimeout(() => {
            executeSearch(searchQuery, requestId, controller);
        }, 150);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [isOpen, searchQuery, executeSearch]);

    if (!isOpen) return null;

    const hasResults = tenorResults.length > 0 || giphyResults.length > 0;

    return (
        <div
            className={`fixed inset-0 bg-black/80 backdrop-blur-lg flex items-center justify-center p-4 animate-fade-in ${zIndexClass}`}
            onClick={onClose}
        >
            <div
                className="bg-[#0e0e11] border border-white/10 rounded-[2.5rem] w-full max-w-lg h-[80vh] overflow-hidden flex flex-col shadow-2xl backdrop-blur-md"
                style={{ backgroundColor: modalBg, color: userTextColor }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-5 flex items-center justify-between border-b border-white/5">
                    <div className="flex items-center gap-2">
                        <SparklesIcon className="w-5 h-5 animate-pulse" style={{ color: userThemeColor }} />
                        <h3
                            className="text-sm font-black uppercase tracking-tight"
                            style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}
                        >
                            {title}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
                        style={{ color: userTextColor }}
                    >
                        <CloseIcon className="w-4 h-4" />
                    </button>
                </div>

                {/* Controls section (Search input) */}
                <div className="p-4 border-b border-white/5 bg-black/20 border-white/10">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search GIFs..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-2xl py-3 pl-10 pr-4 text-xs outline-none shadow-inner"
                            style={{ color: userTextColor }}
                        />
                        <SearchIcon
                            className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2"
                            style={{ color: userThemeColor }}
                        />
                    </div>
                </div>

                {/* Results columns */}
                <div className="flex-1 overflow-y-auto p-4 allow-scroll">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full py-12">
                            <div
                                className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mb-3"
                                style={{ borderColor: userThemeColor, borderTopColor: 'transparent' }}
                            />
                            <span
                                className="text-[10px] font-black uppercase tracking-wider"
                                style={{ color: userThemeColor, ...stickerTextStyle(0.18) }}
                            >
                                Searching GIFs...
                            </span>
                        </div>
                    ) : !hasResults ? (
                        <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                            <span className="text-[10px] uppercase tracking-widest opacity-40" style={stickerTextStyle(0.18)}>
                                {searchQuery.trim() ? 'No GIFs found' : 'Type to search GIFs'}
                            </span>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            {/* Left Column: Tenor */}
                            <div className="flex flex-col gap-2.5">
                                {tenorResults.map((item) => (
                                    <div
                                        key={item.id}
                                        onClick={() => {
                                            onSelectGif(item.url, item);
                                            onClose();
                                        }}
                                        className="relative aspect-video bg-black/40 rounded-xl overflow-hidden cursor-pointer border border-blue-500/20 hover:border-blue-400/60 transition-all transform active:scale-95 group shadow-md"
                                    >
                                        <img
                                            src={item.previewUrl || item.url}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            alt={item.name}
                                            loading="lazy"
                                        />
                                        <div className="absolute top-1.5 left-1.5 z-10 pointer-events-none">
                                            <span
                                                className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full shadow-md backdrop-blur-md border bg-blue-600/80 text-blue-100 border-blue-400/40"
                                                style={stickerTextStyle(0.12)}
                                            >
                                                tenor
                                            </span>
                                        </div>
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2 pointer-events-none">
                                            <span className="text-[9px] font-bold text-white truncate w-full" style={stickerTextStyle(0.15)}>
                                                {item.name}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Right Column: Giphy */}
                            <div className="flex flex-col gap-2.5">
                                {giphyResults.map((item) => (
                                    <div
                                        key={item.id}
                                        onClick={() => {
                                            onSelectGif(item.url, item);
                                            onClose();
                                        }}
                                        className="relative aspect-video bg-black/40 rounded-xl overflow-hidden cursor-pointer border border-pink-500/20 hover:border-pink-400/60 transition-all transform active:scale-95 group shadow-md"
                                    >
                                        <img
                                            src={item.previewUrl || item.url}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            alt={item.name}
                                            loading="lazy"
                                        />
                                        <div className="absolute top-1.5 left-1.5 z-10 pointer-events-none">
                                            <span
                                                className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full shadow-md backdrop-blur-md border bg-pink-600/80 text-pink-100 border-pink-400/40"
                                                style={stickerTextStyle(0.12)}
                                            >
                                                giphy
                                            </span>
                                        </div>
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2 pointer-events-none">
                                            <span className="text-[9px] font-bold text-white truncate w-full" style={stickerTextStyle(0.15)}>
                                                {item.name}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GifPickerModal;
